'use client'

import { useAction } from '@/shared/config/action'
import { useServerErrorHandler } from '@/shared/config/action'

import { generateUploadUrl as _generateUploadUrl } from './generate-upload-url/request'
import {
  TGenerateUploadUrl,
  TGenerateUploadUrlResponse,
} from './generate-upload-url/schema'
import { generateUploadUrls as _generateUploadUrls } from './generate-upload-urls/request'
import {
  TGenerateUploadUrls,
  TGenerateUploadUrlsResponse,
} from './generate-upload-urls/schema'

export const useUploadUrl = ({
  onSuccess,
}: {
  onSuccess?: (data: TGenerateUploadUrlResponse) => void
} = {}) => {
  const { handleServerError } = useServerErrorHandler()

  const action = useAction(_generateUploadUrl, {
    onSuccess: ({ data }) => {
      if (data) {
        onSuccess?.(data)
      }
    },
    onError: ({ error }) => {
      if (error.serverError) {
        handleServerError(error.serverError)
      }
    },
  })

  const generateUploadUrl = async (file: File, body: TGenerateUploadUrl) => {
    const res = await action.executeAsync(body)

    if (res?.data?.data) {
      const { presignedUrl, contentType, objectKey } = res.data.data

      try {
        await fetch(presignedUrl, {
          method: 'PUT',
          headers: { 'Content-Type': contentType },
          body: file,
        })

        return objectKey
      } catch (error) {
        handleServerError(error)
      }
    }

    return null
  }

  return { generateUploadUrl }
}

export const useUploadUrls = ({
  onSuccess,
}: {
  onSuccess?: (data: TGenerateUploadUrlsResponse) => void
} = {}) => {
  const { handleServerError } = useServerErrorHandler()

  const action = useAction(_generateUploadUrls, {
    onSuccess: ({ data }) => {
      if (data) {
        onSuccess?.(data)
      }
    },
    onError: ({ error }) => {
      if (error.serverError) {
        handleServerError(error.serverError)
      }
    },
  })

  const generateUploadUrls = async (
    files: File[],
    body: TGenerateUploadUrls,
  ) => {
    const res = await action.executeAsync(body)

    if (res?.data?.data) {
      const { presignedUrls } = res.data.data

      try {
        await Promise.all(
          presignedUrls.map(({ presignedUrl, contentType }, idx) => {
            return fetch(presignedUrl, {
              method: 'PUT',
              headers: { 'Content-Type': contentType },
              body: files[idx],
            })
          }),
        )

        return presignedUrls.map(({ objectKey }) => objectKey)
      } catch (error) {
        handleServerError(error)
      }
    }

    return null
  }

  return { generateUploadUrls }
}
